import { formatPrice } from '@/common/constant';
import type { WineProductSearchResult } from '@/common/domain-model/product-search';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { Card } from '@/pages/live-chat/components/card';
import { BulbOutlined } from '@ant-design/icons';
import { Pagination, Select, Tag, Tooltip } from 'antd';
import {
  Component,
  type ErrorInfo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface WineSearchResultsProps {
  products: WineProductSearchResult[];
  className?: string;
  isLoading?: boolean;
  onError?: (error: Error) => void;
}

function WineProductCard({
  product,
}: {
  product: WineProductSearchResult;
}) {
  const { similarity } = product;
  const { currency } = useCurrencySymbol();
  const rating = product.data.rating;
  const matchPercentage = `${(similarity * 100).toFixed(2)}%`;
  const ratingColor = useMemo(() => {
    if (rating && rating >= 90) return 'text-green-600';
    if (rating && rating >= 80) return 'text-blue-600';
    if (rating && rating >= 70) return 'text-yellow-600';
    return 'text-gray-600';
  }, [rating]);

  const imageUrl = product.data.image_urls?.[0];

  return (
    <Card className="p-4 flex gap-4 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 hover:bg-muted/50 transition-colors">
      {imageUrl && (
        <img
          src={imageUrl}
          alt={product.data.title}
          className="w-24 h-32 object-cover rounded-md bg-muted shrink-0"
          loading="lazy"
        />
      )}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold break-words pr-4">
              {product.data.title}
            </h3>
          </div>
          <div className="flex flex-col items-end text-sm shrink-0">
            {rating && (
              <div className="flex items-center gap-1">
                <span className={`font-medium ${ratingColor}`}>
                  {rating.toFixed(1)}
                </span>
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Match: {matchPercentage}
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground break-words">
          {product.data.description || 'No description available'}
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          {[
            {
              id: 'wine_type',
              label: product.data.wine_type,
              value: product.data.wine_type,
            },
            {
              id: 'grape',
              label: product.data.grape,
              value: product.data.grape,
            },
            {
              id: 'producer',
              label: product.data.producer,
              value: product.data.producer,
            },
            {
              id: 'country',
              label: product.data.country,
              value: product.data.country,
            },
            { id: 'year', label: product.data.year, value: product.data.year },
            {
              id: 'alcohol',
              label: product.data.alcohol,
              value: product.data.alcohol
                ? `${product.data.alcohol}% ABV`
                : undefined,
            },
            {
              id: 'source',
              label: product.source_name,
              value: product.source_name,
            },
            {
              id: 'supplier',
              label: product.supplier_name,
              value: product.supplier_name,
            },
          ].map((attr) =>
            attr.label && attr.value ? (
              <Tag
                color="#18181b1a"
                className="!text-black"
                key={`${attr.id}-${attr.value}`}
                bordered={false}
              >
                {attr.value}
              </Tag>
            ) : null,
          )}
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="font-semibold shrink-0">
            {product.data.price
              ? formatPrice(product.data.price, currency)
              : 'Price not available'}
          </span>
          {/* {product.data.product_urls?.[0] && (
            <a
              href={product.data.product_urls[0]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1 hover:bg-primary/10 transition-colors shrink-0"
            >
              View Details
            </a>
          )} */}
        </div>
      </div>
    </Card>
  );
}

function WineProductSkeleton() {
  return (
    <Card className="p-4 flex gap-4">
      <div className="w-24 h-32 bg-muted animate-pulse rounded-md" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="h-6 bg-muted animate-pulse rounded w-2/3" />
          <div className="flex flex-col items-end gap-1">
            <div className="h-4 bg-muted animate-pulse rounded w-16" />
            <div className="h-3 bg-muted animate-pulse rounded w-12" />
          </div>
        </div>
        <div className="h-4 bg-muted animate-pulse rounded w-full" />
        <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
        <div className="flex gap-2">
          <div className="h-6 bg-muted animate-pulse rounded-full w-20" />
          <div className="h-6 bg-muted animate-pulse rounded-full w-24" />
        </div>
        <div className="flex justify-between items-center">
          <div className="h-5 bg-muted animate-pulse rounded w-16" />
          <div className="h-4 bg-muted animate-pulse rounded w-24" />
        </div>
      </div>
    </Card>
  );
}

type SortOption = 'similarity' | 'rating' | 'price';

function KeyboardShortcutsInfo() {
  return (
    <Tooltip
      title={
        <div className="text-sm">
          <p className="font-medium mb-1">Keyboard Shortcuts</p>
          <div className="space-y-1">
            <p>← Previous page</p>
            <p>→ Next page</p>
          </div>
        </div>
      }
    >
      <button
        type="button"
        className="p-1 hover:bg-muted rounded-md"
        aria-label="Keyboard shortcuts"
      >
        <BulbOutlined className="h-4 w-4 text-muted-foreground" />
      </button>
    </Tooltip>
  );
}

interface ErrorBoundaryProps {
  onError?: (error: Error) => void;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error in WineSearchResults:', error, errorInfo);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="p-4">
          <div className="text-center text-red-600">
            An error occurred while displaying search results. Please try again.
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}

function WineProductList({
  products,
  isLoading,
}: {
  products: WineProductSearchResult[];
  isLoading: boolean;
}) {
  return (
    <div className="grid gap-4">
      {products.map((product) => (
        <div
          key={product.id}
          className="transform transition-all duration-300 ease-in-out"
          style={{
            opacity: isLoading ? 0.5 : 1,
            transform: isLoading ? 'translateY(10px)' : 'translateY(0)',
          }}
        >
          <WineProductCard product={product} />
        </div>
      ))}
    </div>
  );
}

export function WineSearchResults({
  products,
  className = '',
  isLoading = false,
  onError,
}: WineSearchResultsProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>('similarity');
  const itemsPerPage = 4;
  const searchResultsRef = useRef<HTMLDivElement>(null);

  // Reset page when products change
  const productsRef = useRef<WineProductSearchResult[]>(products);
  useEffect(() => {
    if (productsRef.current !== products) {
      productsRef.current = products;
      setCurrentPage(1);
    }
  }, [products]);

  // Add keyboard shortcuts for pagination
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore keyboard shortcuts when loading or when user is typing
      if (
        isLoading ||
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
          }
          break;
        case 'ArrowRight':
          if (currentPage < Math.ceil(products.length / itemsPerPage)) {
            setCurrentPage(currentPage + 1);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, products.length, isLoading]);

  // Memoize sorted products
  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'similarity':
        sorted.sort((a, b) => b.similarity - a.similarity);
        break;
      case 'rating':
        sorted.sort((a, b) => {
          const ratingA = a.data.rating ?? 0;
          const ratingB = b.data.rating ?? 0;
          return ratingB - ratingA;
        });
        break;
      case 'price':
        sorted.sort((a, b) => {
          const priceA = a.data.price ?? 0;
          const priceB = b.data.price ?? 0;
          return priceB - priceA;
        });
        break;
    }
    return sorted;
  }, [products, sortBy]);

  // Memoize pagination values
  const { totalPages, currentProducts } = useMemo(() => {
    const total = Math.ceil(sortedProducts.length / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return {
      totalPages: total,
      currentProducts: sortedProducts.slice(start, end),
    };
  }, [sortedProducts, currentPage]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll search results into view after page change
    setTimeout(() => {
      searchResultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  }, []);

  // Add animation state
  const [isSorting, setIsSorting] = useState(false);

  const handleSortChange = useCallback((value: string) => {
    setIsSorting(true);
    setSortBy(value as SortOption);
    setCurrentPage(1);
    // Reset sorting state after animation
    setTimeout(() => setIsSorting(false), 300);
  }, []);

  if (isLoading) {
    return (
      <Card
        className={`p-4 border-2 border-primary/30 shadow-md relative ${className}`}
      >
        <div className="absolute -top-3 left-4 bg-orange-100 text-orange-700 px-2 py-0.5 text-sm rounded-full font-medium">
          Search Results
        </div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center gap-2 min-w-[200px] flex-1">
            <p className="text-sm text-muted-foreground animate-pulse whitespace-nowrap">
              Searching for matching wines...
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Sort by:
            </span>
            <Select
              disabled={isLoading}
              value={sortBy}
              onChange={handleSortChange}
              style={{ width: 180 }}
              options={[
                { value: 'similarity', label: 'Best Match' },
                { value: 'rating', label: 'Highest Rated' },
                { value: 'price', label: 'Price: Low to High' },
              ]}
            />
          </div>
        </div>
        <div className="min-w-0">
          <div className="grid gap-4 pt-2">
            {Array.from(
              { length: 4 },
              (_, i) => `skeleton-${i}-${Date.now()}`,
            ).map((key) => (
              <WineProductSkeleton key={key} />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (products.length === 0) {
    return (
      <Card
        className={`p-4 border-2 border-primary/30 shadow-md relative ${className}`}
      >
        <div className="absolute -top-3 left-4 bg-orange-100 text-orange-700 px-2 py-0.5 text-sm rounded-full font-medium">
          Search Results
        </div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center gap-2 min-w-[200px] flex-1">
            <p className="text-sm text-muted-foreground whitespace-nowrap">
              No matching wines found
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Sort by:
            </span>
            <Select
              disabled={isLoading}
              value={sortBy}
              onChange={handleSortChange}
              style={{ width: 180 }}
              options={[
                { value: 'similarity', label: 'Best Match' },
                { value: 'rating', label: 'Highest Rated' },
                { value: 'price', label: 'Price: Low to High' },
              ]}
            />
          </div>
        </div>
        <div className="text-center text-muted-foreground py-8">
          <p className="max-w-[500px] mx-auto">
            No matching wines found. Try adjusting your search criteria.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <ErrorBoundary onError={onError}>
      <div
        ref={searchResultsRef}
        className={`space-y-6 w-full mt-10 ${className}`}
      >
        <Card className="p-4 border-2 border-primary/30 shadow-md relative">
          <div className="absolute -top-3 left-4 bg-orange-100 text-orange-700 px-2 py-0.5 text-sm rounded-full font-medium">
            Search Results
          </div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div className="flex items-center gap-2 min-w-[200px] flex-1">
              <p className="text-sm text-muted-foreground whitespace-nowrap">
                Found {products.length} matching wines
              </p>
              <KeyboardShortcutsInfo />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Sort by:
              </span>
              <Select
                disabled={isLoading}
                value={sortBy}
                onChange={handleSortChange}
                style={{ width: 180 }}
                options={[
                  { value: 'similarity', label: 'Best Match' },
                  { value: 'rating', label: 'Highest Rated' },
                  { value: 'price', label: 'Price: Low to High' },
                ]}
              />
            </div>
          </div>
          <div className="min-w-0">
            <WineProductList products={currentProducts} isLoading={isSorting} />
          </div>

          {totalPages > 1 && (
            <div className="mt-4 pt-4 border-t">
              <Pagination
                current={currentPage}
                total={products.length}
                pageSize={itemsPerPage}
                onChange={handlePageChange}
                disabled={isLoading}
                showSizeChanger={false}
                className="flex justify-center"
              />
            </div>
          )}
        </Card>
      </div>
    </ErrorBoundary>
  );
}
